import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield, CheckCircle, ArrowRight, FileSearch, Database, FileText,
  Lock, Users, AlertTriangle, UserCheck, Eye, BarChart3, Building2,
  Sparkles, BookOpen, Settings, ClipboardCheck, Layers
} from 'lucide-react';

const DPDPPage = () => {
  const services = [
    {
      icon: FileSearch,
      title: 'DPDP Readiness Assessment',
      description: 'Understand where your organization stands today. We evaluate your current privacy practices, identify compliance gaps, and provide a clear roadmap to achieve DPDP readiness.',
      deliverables: [
        'Compliance maturity assessment',
        'Risk and gap analysis',
        'Executive summary',
        'Prioritized implementation roadmap',
      ],
    },
    {
      icon: Database,
      title: 'Personal Data Discovery & Mapping',
      description: 'You cannot protect what you cannot see. We help identify where personal data exists across your organization and document how it moves through your business processes.',
      deliverables: [
        'Personal data inventory',
        'Data flow mapping',
        'Data classification',
        'Sensitive data identification',
      ],
    },
    {
      icon: Eye,
      title: 'Privacy Gap Assessment',
      description: 'Our consultants review your existing policies, business processes, applications, websites, employee practices, and vendor ecosystem to identify areas requiring improvement.',
      deliverables: [
        'Websites and customer portals',
        'Mobile applications',
        'HR processes',
        'Marketing and CRM platforms',
        'Third-party vendors',
        'Cloud environments',
        'Internal business workflows',
      ],
    },
    {
      icon: FileText,
      title: 'Policy & Governance Framework',
      description: 'Develop comprehensive privacy documentation aligned with the DPDP Act and industry best practices.',
      deliverables: [
        'Privacy Policy',
        'Employee Privacy Policy',
        'Data Retention Policy',
        'Consent Management Policy',
        'Data Classification Standard',
        'Incident Response Plan',
        'Vendor Data Processing Guidelines',
      ],
    },
    {
      icon: UserCheck,
      title: 'Consent Management',
      description: 'Ensure customer consent is collected, managed, and maintained in accordance with regulatory requirements.',
      deliverables: [
        'Consent collection mechanisms',
        'Consent withdrawal processes',
        'Audit trails',
        'Preference management',
        'Cookie and website consent reviews',
      ],
    },
    {
      icon: Layers,
      title: 'Data Lifecycle Management',
      description: 'Create governance processes that define how personal data is collected, stored, accessed, retained, archived, and securely deleted throughout its lifecycle.',
      deliverables: [],
    },
    {
      icon: Building2,
      title: 'Vendor & Third-Party Privacy Assessments',
      description: 'Third-party vendors often introduce significant privacy risks. We assess your vendor ecosystem and recommend controls to ensure responsible handling of personal data.',
      deliverables: [],
    },
    {
      icon: Users,
      title: 'Employee Awareness & Training',
      description: 'Technology alone cannot achieve compliance. We deliver practical training programs that help employees understand their responsibilities and foster a privacy-first culture.',
      deliverables: [
        'Executive Leadership',
        'HR Teams',
        'IT & Security',
        'Sales & Marketing',
        'Customer Support',
        'Organization-wide Awareness',
      ],
    },
    {
      icon: AlertTriangle,
      title: 'Incident Response & Breach Readiness',
      description: 'Prepare your organization to respond effectively in the event of a privacy incident.',
      deliverables: [
        'Privacy incident response planning',
        'Escalation procedures',
        'Response playbooks',
        'Tabletop exercises',
        'Governance recommendations',
      ],
    },
    {
      icon: Shield,
      title: 'Virtual Data Protection Officer (vDPO)',
      description: 'Access ongoing privacy expertise without the cost of hiring a full-time resource.',
      deliverables: [
        'Ongoing compliance advisory',
        'Periodic health checks',
        'Policy reviews',
        'Privacy impact guidance',
        'Regulatory updates',
        'Executive reporting',
      ],
    },
  ];

  const phases = [
    { title: 'Discover', description: 'Understand your business, systems, and data landscape.' },
    { title: 'Assess', description: 'Identify privacy risks and compliance gaps.' },
    { title: 'Design', description: 'Build practical governance frameworks and implementation plans.' },
    { title: 'Implement', description: 'Support policy development, technology enablement, and organizational readiness.' },
    { title: 'Sustain', description: 'Continuously monitor, improve, and adapt your privacy program as regulations evolve.' },
  ];

  const industries = [
    'Banking & Financial Services',
    'Healthcare & Life Sciences',
    'Information Technology',
    'SaaS & Software Products',
    'eCommerce & Retail',
    'Manufacturing',
    'Education',
    'Professional Services',
    'Government & Public Sector',
  ];

  const whyUs = [
    'Reduce compliance risk',
    'Strengthen customer trust',
    'Improve operational efficiency',
    'Establish scalable governance',
    'Integrate privacy into everyday business processes',
    'Prepare for future regulatory and AI governance requirements',
  ];

  return (
    <main className="pt-20">
      {/* Hero */}
      <section className="relative py-32 bg-brand-charcoal overflow-hidden" data-testid="dpdp-hero">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_25%,rgba(92,45,145,0.3),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_75%,rgba(196,158,77,0.2),transparent_50%)]" />
        </div>

        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ y: [0, -15, 0], rotate: [0, 2, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-1/4 right-1/5 w-52 h-52 bg-brand-purple/10 rounded-full blur-3xl"
          />
          <motion.div
            animate={{ y: [0, 12, 0], rotate: [0, -2, 0] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            className="absolute bottom-1/3 left-1/6 w-60 h-60 bg-brand-gold/10 rounded-full blur-3xl"
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 bg-brand-gold/20 border border-brand-gold/30 rounded-full px-4 py-2 mb-6"
            >
              <Shield className="w-4 h-4 text-brand-gold" />
              <span className="text-brand-gold text-sm font-medium">DPDP Compliance</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6"
            >
              Digital Personal Data{' '}
              <span className="relative">
                <span className="text-brand-gold">Protection</span>
                <motion.span
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                  className="absolute bottom-1 left-0 h-1 bg-brand-gold/40"
                />
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-2xl text-brand-gold-lite font-light italic mb-4"
            >
              Build Trust. Reduce Risk. Stay Compliant.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-lg text-gray-300 max-w-3xl leading-relaxed mb-10"
            >
              India's Digital Personal Data Protection (DPDP) Act has transformed the way organizations 
              collect, process, store, and manage personal data. Whether you're a startup, SME, enterprise, 
              healthcare provider, educational institution, or eCommerce business, compliance is no longer 
              optional. It is a business imperative.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-charcoal font-semibold px-10 py-4 hover:bg-brand-gold-lite hover:scale-105 transition-all duration-300 group"
                data-testid="dpdp-hero-cta"
              >
                Start Your Compliance Journey
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why DPDP Compliance Matters */}
      <section className="py-24 bg-brand-bg-light relative overflow-hidden" data-testid="dpdp-why">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-brand-lavender/50 to-transparent pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block bg-brand-purple/10 text-brand-purple text-sm font-semibold px-4 py-2 rounded-full mb-4">
                Why It Matters
              </span>
              <h2 className="font-heading text-4xl sm:text-5xl font-bold text-brand-charcoal mb-6">
                Why DPDP Compliance <span className="text-brand-purple">Matters</span>
              </h2>
              <p className="text-brand-body text-lg leading-relaxed mb-8">
                Every organization handles personal data — whether it belongs to customers, employees, 
                partners, or vendors. Without the right governance and controls, businesses face increasing 
                regulatory obligations, operational challenges, and reputational risks.
              </p>
              <p className="text-brand-body text-lg leading-relaxed">
                At Expertize Edge, we help organizations navigate the complexities of the DPDP Act with 
                a practical, technology-driven approach. Our goal is simple — enable your business to 
                achieve compliance while strengthening customer trust, improving data governance, and 
                minimizing regulatory risk.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="space-y-4">
                {[
                  'Understand your obligations under the DPDP Act',
                  'Identify compliance gaps before they become business risks',
                  'Protect personal data across your organization',
                  'Establish clear governance and accountability',
                  'Build customer confidence through responsible data practices',
                  'Create a sustainable privacy program that evolves with your business',
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.08 }}
                    className="flex items-start gap-4 bg-white p-5 rounded-xl border border-brand-lavender hover:border-brand-purple/20 hover:shadow-lg transition-all duration-300"
                  >
                    <div className="w-8 h-8 bg-brand-purple/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-5 h-5 text-brand-purple" />
                    </div>
                    <span className="text-brand-body font-medium">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white" data-testid="dpdp-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block bg-brand-gold/10 text-brand-gold text-sm font-semibold px-4 py-2 rounded-full mb-4">
              Comprehensive Solutions
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-brand-charcoal mb-4">
              Our DPDP <span className="text-brand-purple">Compliance Services</span>
            </h2>
            <p className="text-brand-muted text-lg max-w-2xl mx-auto">
              End-to-end services designed to take your organization from assessment to full compliance.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.06 }}
                className="group"
                data-testid={`dpdp-service-${index}`}
              >
                <div className="bg-brand-bg-light rounded-2xl p-8 h-full border border-transparent hover:border-brand-purple/20 hover:shadow-xl transition-all duration-300 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-brand-purple/5 to-brand-gold/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                  <div className="relative">
                    <div className="w-14 h-14 bg-gradient-to-br from-brand-purple to-brand-purple-mid rounded-2xl flex items-center justify-center mb-5 transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                      <service.icon className="w-7 h-7 text-white" />
                    </div>

                    <h3 className="font-heading text-xl font-bold text-brand-charcoal mb-3">
                      {service.title}
                    </h3>
                    <p className="text-brand-muted leading-relaxed mb-4">
                      {service.description}
                    </p>

                    {service.deliverables.length > 0 && (
                      <div className="space-y-2">
                        {service.deliverables.map((item, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-brand-gold flex-shrink-0" />
                            <span className="text-brand-body text-sm">{item}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-24 bg-brand-charcoal relative overflow-hidden" data-testid="dpdp-approach">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(196,158,77,0.1),transparent_60%)]" />
        </div>
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }} />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-flex items-center gap-2 bg-brand-gold/20 border border-brand-gold/30 rounded-full px-4 py-2 mb-4">
              <Settings className="w-4 h-4 text-brand-gold" />
              <span className="text-brand-gold text-sm font-medium">Our Approach</span>
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">
              A <span className="text-brand-gold">Proven</span> Five-Phase Model
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              We believe compliance should enable business growth, not slow it down.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {phases.map((phase, index) => (
              <motion.div
                key={phase.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.12 }}
                className="relative group"
              >
                {index < phases.length - 1 && (
                  <div className="hidden md:block absolute top-10 left-[calc(50%+2rem)] w-[calc(100%-4rem)] h-0.5 bg-white/10" />
                )}

                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-brand-gold to-brand-gold-lite text-brand-charcoal rounded-2xl flex items-center justify-center mx-auto mb-4 text-2xl font-bold shadow-lg transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                    {index + 1}
                  </div>
                  <h3 className="font-heading text-xl font-bold text-white mb-2">
                    {phase.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{phase.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-brand-bg-light relative overflow-hidden" data-testid="dpdp-why-us">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block bg-brand-purple/10 text-brand-purple text-sm font-semibold px-4 py-2 rounded-full mb-4">
                The Difference
              </span>
              <h2 className="font-heading text-4xl sm:text-5xl font-bold text-brand-charcoal mb-6">
                Why Choose <span className="text-brand-purple">Expertize Edge</span>?
              </h2>
              <p className="text-brand-body text-lg leading-relaxed mb-8">
                Unlike traditional compliance consultants, we combine expertise in privacy, cybersecurity, 
                artificial intelligence, cloud technologies, and enterprise engineering to deliver practical, 
                business-focused solutions.
              </p>

              <div className="space-y-4">
                {whyUs.map((item, index) => (
                  <motion.div
                    key={index}
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.08 }}
                  >
                    <div className="w-6 h-6 rounded-full bg-brand-gold/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-brand-gold" />
                    </div>
                    <span className="text-brand-body font-medium">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-brand-charcoal rounded-2xl p-8 text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(196,158,77,0.15),transparent_50%)]" />

                <div className="relative">
                  <span className="inline-flex items-center gap-2 bg-brand-gold/20 border border-brand-gold/30 rounded-full px-4 py-2 mb-6">
                    <Building2 className="w-4 h-4 text-brand-gold" />
                    <span className="text-brand-gold text-sm font-medium">Industries We Serve</span>
                  </span>

                  <div className="grid grid-cols-1 gap-3">
                    {industries.map((industry, idx) => (
                      <motion.div
                        key={idx}
                        className="flex items-center gap-3"
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: idx * 0.06 }}
                      >
                        <CheckCircle className="w-4 h-4 text-brand-gold flex-shrink-0" />
                        <span className="text-gray-300">{industry}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-brand-charcoal to-brand-purple relative overflow-hidden" data-testid="dpdp-cta">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }} />
        </div>

        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6">
              Ready to Become DPDP Compliant?
            </h2>
            <p className="text-white/80 text-lg mb-4 max-w-2xl mx-auto">
              Whether you're starting your compliance journey or strengthening an existing privacy program, 
              our experts can help you develop a practical roadmap tailored to your organization.
            </p>
            <p className="text-white/70 text-base mb-10 max-w-2xl mx-auto italic">
              Let's build a privacy-first organization that earns customer trust, strengthens governance, 
              and ensures your business is ready for the future.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-charcoal font-semibold px-10 py-4 hover:bg-brand-gold-lite hover:scale-105 transition-all duration-300 group"
                data-testid="dpdp-cta-contact"
              >
                Book a Consultation
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center justify-center gap-2 border-2 border-white/30 text-white font-semibold px-10 py-4 hover:bg-white/10 hover:border-white/50 transition-all duration-300"
              >
                View All Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default DPDPPage;
